import _ from "lodash";
import React from "react";
import EmojiPicker from "UIComponents/EmojiPicker";
import I18nHOC from "UIComponents/I18nHOC";
import EmptyView from "UIComponents/EmptyView";
import MathEquationEditor from "UIComponents/MathEquationEditor";
import PropTypes from "prop-types";
import { Button, FormInputError } from "@toddle-edu/ds-web";
import classes from "./RichTextEditor.scss";
import UIBaseComponent from "UIComponents/UIBaseComponent";
import ACLStore from "lib/aclStore";
import LinkifyModal from "./components/LinkifyModal";
import { getAIResponse } from "../../modules/Services";
import { NoFoldersIllustration } from "@toddle-edu/ds-theme";
import classNames from "classnames";
import { uploadFileV2, openLinkInNewTab } from "modules/Services";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { connect } from "react-redux";
import { compose, graphql } from "lib/apolloClient";
import { getJsonItem } from "services/localStorage";
import { getAIPromptsQuery } from "modules/CommonQuery";
import { getAIPromptsFromCache } from "modules/CommonGraphqlHelpers";
import { getSelectedCurriculumId } from "modules/CommonSelectors";
import AIFeedbackStrip from "AppComponents/AIFeedbackStrip";
import ConfigureAIVoice from "AppComponents/ConfigureAIVoice";
import { getBasicDeviceInfo } from "Utils";
import { AI_TRANSLATION_LANGUAGES } from "@toddle-edu/common-utils";
import { openChat } from "Login/modules/LoginModule";
import { isAIModuleMarketingEnabled } from "Platform/modules/PlatformUtils";
import { setAiModuleMarketingKey } from "Platform/modules/PlatformModule";
import { getCloudUrl } from "Constants";
import TheatreModeV2 from "UIComponents/TheatreModeV2/TheatreModeV2";
import SkeletonLoader from "UIComponents/SkeletonLoader";
import { allowURLWithCredentials } from "Utils/SecureUrlHelpers";
import docEditorStore from "../DocEditor/docEditorStore";

const getType = curriculumProgram => {
  if (curriculumProgram) {
    return curriculumProgram.type;
  } else {
    return null;
  }
};

class RichTextEditor extends UIBaseComponent {
  // Static variable to track which instance has an open equation editor
  static openEquationEditorInstance = null;
  errorId = _.uniqueId("form-error-");

  state = {
    ...this.state,
    isPickerOpen: false,
    pickedEmoji: "",
    isEditorLoading: true,
    isEventListenerAdded: false,
    isEquationEditorOpen: false,
    equationAttributes: "",
    latex: "",
    imgSrc: "",
    isLatexImageInsertInProgress: false,
    isLinkModalOpen: false,
    linkModalUrl: "",
    linkModalTitle: "",
    linkParams: {},
    curriculumProgramType: getType(getJsonItem("currentCurriculumProgram")),
    hasContent: false,
    selectedText: "",
    DocumentEditor: null, // For lazy loading DocumentEditor
    isEditorLoadingError: null,
    isDocEditorMounted: false,
  };
  containerRef = React.createRef();
  emojiButtonRef = React.createRef();
  rteRef = React.createRef();

  changeValue = val => {
    this.updateValue(val);
  };

  getViewValue = () => {
    return this.props.value ? this.props.value : "N/A";
  };

  lazyLoadDocEditor = () => {
    import(/* webpackChunkName: "doc-editor" */ "@toddle-edu/doc-editor")
      .then(module => {
        this.setState({
          DocumentEditor: module.DocumentEditor,
          isEditorLoading: false,
        });
      })
      .catch(error => {
        console.error("Failed to load DocumentEditor:", error);
        this.setState({
          isEditorLoading: false,
          isEditorLoadingError: error,
        });
      });
  };

  componentDidMount() {
    const deviceInfo = getBasicDeviceInfo();
    const name = deviceInfo?.browser?.name;
    let showUpdateEqButton = false;
    if (name.toLowerCase() === "chrome") {
      showUpdateEqButton = true;
    }
    this.setState({
      allowReInsertImageAutomation: ACLStore.can(
        "FeatureFlag:ShowMathEqAutoFixButton"
      ),
      allowEqCreationViaSelection: ACLStore.can(
        "FeatureFlag:GenerateMathEqOnSelection"
      ),
      showUpdateEqButton,
      isDocEditorMounted: docEditorStore.getState(),
    });

    // Subscribe to docEditorStore
    this.unsubscribeDocEditorStore = docEditorStore.subscribe(() => {
      this.setState({ isDocEditorMounted: docEditorStore.getState() });
    });

    // Lazy load the DocumentEditor component from @toddle-edu/doc-editor
    this.lazyLoadDocEditor();
  }

  componentWillUnmount() {
    // Unsubscribe from docEditorStore
    if (this.unsubscribeDocEditorStore) {
      this.unsubscribeDocEditorStore();
    }
  }

  onEmojiSelect = e => {
    const { native: emoji } = e;
    this.setState({ pickedEmoji: emoji });
  };

  /**
   * Imperative handler for inserting equations
   * @param {Object} equationData - The equation data to insert
   */
  handleEquationInsertion = equationData => {
    if (this.rteRef.current?.insertEquation) {
      this.rteRef.current.insertEquation(equationData);
    }
  };

  /**
   * Imperative handler for inserting links
   * @param {Object} linkData - The link data to insert
   */
  handleLinkInsertion = linkData => {
    if (this.rteRef.current?.insertLink) {
      this.rteRef.current.insertLink(linkData);
    }
  };

  handleUpdateImage = ({ oldUrl, newUrl, height, width, verticalAlign }) => {
    if (this.rteRef.current?.updateImageUrl) {
      this.rteRef.current.updateImageUrl({
        oldUrl,
        newUrl,
        height,
        width,
        verticalAlign,
      });
    }
  };

  handleInsertEquation = ({ height, width, latex, verticalAlign, src }) => {
    const equationAttributes = {
      src,
      height: height + "px",
      width: width + "px",
      verticalAlign: verticalAlign + "px",
      latex,
      altText: latex,
      display: "inline",
    };

    this.setState({ equationAttributes });
    this.handleEquationInsertion(equationAttributes);
  };

  handleClickOutside = event => {
    if (
      this.state.isPickerOpen &&
      !this.containerRef?.current?.contains(event.target)
    ) {
      this.setState(prevState => ({
        isPickerOpen: false,
      }));
    }
  };

  isValid = () => {
    const { error } = this.props;
    const errorMessage = error ? error : "Enter some content";

    if (!this.state.hasContent) {
      this.updateErrorState(errorMessage);
    } else {
      this.updateErrorState("");
    }
    return this.state.hasContent ? 0 : 1;
  };

  onClickSaveLink = params => {
    const linkData = {
      url: params.url,
      title: params.title,
      linkType: params.linkType,
      filename: params.filename,
      mimeType: params.mimeType,
      fileType: params.fileType,
    };
    this.setState({
      linkParams: params,
    });
    this.setState({
      linkModalUrl: params.url,
      linkModalTitle: params.title,
    });
    this.onCloseLinkModal();
    // Use imperative handler for insertion
    this.handleLinkInsertion(linkData);
    this.containerRef?.current?.focus();
  };

  onCloseLinkModal = () => {
    this.setState({
      isLinkModalOpen: false,
      linkModalUrl: "",
      linkModalTitle: "",
    });
  };

  renderViewEmpty = () => {
    const { t, emptyComponent, testId } = this.props;

    return emptyComponent ? (
      emptyComponent
    ) : (
      <EmptyView
        emptyImageUrl={NoFoldersIllustration}
        emptyHeaderText={t("common:no_label_added", {
          label: t("common:unit_summary"),
        })}
        size="small"
        testId={testId}
      />
    );
  };

  editorRef = ref => {
    if (ref) {
      this.updateInputRef(ref?.editor?.iframeElement);
    }
  };

  setLatex = latex => {
    this.setState({
      latex,
    });
  };

  closeEditor = () => {
    // Clear the static reference when closing the editor
    if (RichTextEditor.openEquationEditorInstance === this) {
      RichTextEditor.openEquationEditorInstance = null;
    }

    this.setState({
      isEquationEditorOpen: false,
      latex: "",
      latexList: [],
    });
  };

  renderEquationEditor = () => {
    const {
      allowCopyPasteInEquationEditor = true,
      disabledCopyButtonTooltipText = null,
    } = this.props;
    const latexList = this.state.latexList;
    return (
      <MathEquationEditor
        closeEditor={this.closeEditor}
        onInsertButtonClick={this.handleInsertEquation}
        handleUpdateImage={this.handleUpdateImage}
        latex={this.state.latex}
        latexList={
          this.state.allowReInsertImageAutomation && latexList?.length > 0
            ? latexList
            : null
        }
        selectedText={
          this.state.allowEqCreationViaSelection && this.state.selectedText
            ? this.state.selectedText
            : null
        }
        testId={this.props.testId}
        allowCopyPasteInEquationEditor={allowCopyPasteInEquationEditor}
        disabledCopyButtonTooltipText={disabledCopyButtonTooltipText}
      />
    );
  };

  reInsertImages = () => {
    const rte = this.containerRef.current;

    // Getting all the equation img tags from RTE
    var imgTags = rte.querySelectorAll("img[data-latex]");

    const latexList = [];
    imgTags.forEach(imgTag => {
      latexList.push({ latex: imgTag.getAttribute("data-latex"), ele: imgTag });
    });

    if (latexList.length) {
      RichTextEditor.openEquationEditorInstance = this;
      this.setState({
        latexList,
        isEquationEditorOpen: true,
      });
    }
  };

  _onBlur = () => {
    const { value, shouldCallOnBlur } = this.props;
    if (shouldCallOnBlur && value) {
      this.onBlur({ value });
    }
  };

  renderEdit = () => {
    // Check if DocEditorComponent is mounted from state
    if (this.state.isDocEditorMounted) {
      return null;
    }

    const {
      t,
      i18n,
      height,
      value,
      userInfo,
      disabled,
      readOnly,
      lockFieldForSchoolGroups,
      emojiPickerPositions,
      containerRef,
      getAIResponseWrapper, // in case the parent component wants to pass any wrapper around getAIResponse
      getAIResponse,
      prompts,
      promptVariables,
      pluginsConfig = {},
      curriculumProgramId,
      placeholder = "",
      scrollToEnd = false,
      hideErrorMessage,
      module,
      defaultLanguage,
      aiPlaceholder,
      aiModuleForMarketing,
      onSettingsUpdate,
      fontSize,
      isActive = false,
      toolbarPosition = "top",
      isHeightAdaptive = false,
      isToolbarSticky = false,
      testId,
      viewOnly = false,
      showDragHandles = true,
      isShowAllButtonEnabled = true,
    } = this.props;
    const isMathsEquationEnabled =
      pluginsConfig?.mathsEditor?.isEnabled ?? true;

    const errorMessage = this.state.error ? this.state.error : "";

    // Handle locked field for school groups
    if (lockFieldForSchoolGroups) {
      return value ? (
        <div className={classes.lockContainer}>
          <div
            dangerouslySetInnerHTML={{ __html: this.getViewValue() }}
            className={classes.lockContainerText}
          ></div>
        </div>
      ) : (
        this.renderViewEmpty()
      );
    }

    const plugins = {
      image: {
        isEnabled: true,
        allowPaste: true,
      },
      link: {
        isEnabled: true,
      },
      utils: {},
      table: {
        isEnabled: true,
      },
      file: {
        isEnabled: true,
      },
      video: {
        isEnabled: true,
      },
      audio: {
        isEnabled: true,
      },
      ...pluginsConfig,
      toolbar: {
        textColor: {
          isEnabled: true,
        },
        headings: {
          isEnabled: true,
        },
        customElement: null,
        showFullScreenButton: true,
        ...pluginsConfig?.toolbar,
      },
    };

    const containerClass = classNames(
      { [classes.container]: true },
      { [classes.disabledContainer]: disabled }
    );

    const languages = _.map(AI_TRANSLATION_LANGUAGES, language => ({
      key: language.label,
      label: language.label,
    }));

    return (
      <div className={containerClass} ref={this.containerRef}>
        {this.state.allowReInsertImageAutomation &&
          this.state.showUpdateEqButton && (
            <Button
              onClick={this.reInsertImages}
              variant="toddle-buddy"
              size="small"
              style={{
                position: "absolute",
                right: "64px",
                top: "-12px",
                zIndex: "3",
                padding: "8px",
                height: "24px",
              }}
              testId={`${testId}-reinsert`}
            >
              Update eq
            </Button>
          )}
        {this.state.isLinkModalOpen && (
          <LinkifyModal
            onClose={this.onCloseLinkModal}
            onClickSave={this.onClickSaveLink}
            url={this.state.linkModalUrl}
            title={this.state.linkModalTitle}
            linkType={this.state.linkModalLinkType}
            filename={this.state.linkModalFilename}
            mimeType={this.state.linkModalMimeType}
            fileType={this.state.linkModalFileType}
            testId={testId}
          />
        )}
        {this.state.isEquationEditorOpen &&
          RichTextEditor.openEquationEditorInstance === this &&
          isMathsEquationEnabled &&
          this.renderEquationEditor()}
        {this.emojiButtonRef.current && (
          <EmojiPicker
            isOpen={this.state.isPickerOpen}
            onEmojiSelect={e => {
              const { native: emoji } = e;
              this.rteRef.current?.insertEmoji(emoji);
              this.setState(prevState => ({
                isPickerOpen: false,
              }));
            }}
            popoverProps={{
              positions: emojiPickerPositions,
              padding: 0,
              reposition: true,
              isChildrenRefPassed: true,
              childrenRef: { current: this.emojiButtonRef.current },
              containerParent:
                (containerRef && containerRef.current) ||
                this.containerRef.current,
            }}
            onClickOutside={() => this.setState({ isPickerOpen: false })}
          />
        )}

        {this.state.isEditorLoading ? (
          <SkeletonLoader height={height} width={"100%"} />
        ) : this.state.isEditorLoadingError ? (
          <div
            className={classes.errorContainer}
            style={{
              minHeight: height,
            }}
          >
            <div>Failed to load. Please try again.</div>
            <Button
              variant="primary"
              size="small"
              onClick={() => {
                this.setState({
                  isEditorLoading: true,
                  isEditorLoadingError: null,
                });
                this.lazyLoadDocEditor();
              }}
              style={{ marginTop: "10px" }}
            >
              Retry
            </Button>
          </div>
        ) : this.state.DocumentEditor ? (
          <this.state.DocumentEditor
            ref={this.rteRef}
            toolbarPosition={toolbarPosition}
            isHeightAdaptive={isHeightAdaptive}
            isToolbarSticky={isToolbarSticky}
            languages={languages}
            openChat={openChat}
            aiPlaceholder={aiPlaceholder}
            readOnly={readOnly || disabled}
            scrollToEnd={scrollToEnd}
            lang={i18n.language}
            height={height}
            content={value}
            onChange={this.changeValue}
            onFocus={e => this.onFocus({ value })}
            onBlur={this._onBlur}
            prompts={prompts}
            promptVariables={{
              ...promptVariables,
              userInfo: userInfo,
              curriculumProgramId: curriculumProgramId,
            }}
            getAIResponse={
              getAIResponseWrapper ? getAIResponseWrapper : getAIResponse
            }
            plugins={plugins}
            showDragHandles={showDragHandles}
            isShowAllButtonEnabled={isShowAllButtonEnabled}
            aiVoiceConfig={
              <ConfigureAIVoice
                size="small"
                entityType="RTE"
                promptSetEntityType={_.get(promptVariables, "entity")}
                headerSubtitle={t("common:configure_voice_of_ai")}
                onSettingsUpdate={onSettingsUpdate}
              />
            }
            uploadToServer={this.props.uploadFileV2}
            setSelectedText={text => this.setState({ selectedText: text })}
            feedbackStrip={
              <AIFeedbackStrip
                containerStyle={{
                  borderRadius: "0px 0px 8px 8px",
                }}
              />
            }
            hasContent={value =>
              this.setState({
                hasContent: value,
              })
            }
            emojiPickerConfig={{
              isOpen: this.state.isPickerOpen,
              setIsPickerOpen: () => {
                this.setState(prevState => ({
                  isPickerOpen: !prevState.isPickerOpen,
                }));
              },
              pickedEmoji: this.state.pickedEmoji,
              clearPickedEmoji: () => this.setState({ pickedEmoji: "" }),
              ref: this.emojiButtonRef,
              // Add imperative insertion handler for consistency
              onEmojiInsert: emoji => {
                if (this.rteRef.current?.insertEmoji) {
                  this.rteRef.current.insertEmoji(emoji);
                }
              },
            }}
            mathsEditorConfig={
              isMathsEquationEnabled && {
                isOpen: this.state.isEquationEditorOpen,
                setIsMathsEditorOpen: () => {
                  const equationEditorInstance =
                    RichTextEditor.openEquationEditorInstance;

                  if (
                    !this.state.isEquationEditorOpen &&
                    equationEditorInstance &&
                    equationEditorInstance !== this
                  ) {
                    equationEditorInstance.closeEditor();
                  }

                  if (!this.state.isEquationEditorOpen) {
                    RichTextEditor.openEquationEditorInstance = this;
                  } else {
                    RichTextEditor.openEquationEditorInstance = null;
                  }

                  this.setState(prevState => ({
                    isEquationEditorOpen: !prevState.isEquationEditorOpen,
                    latex: "",
                  }));
                },
                equationAttributes: this.state.equationAttributes,
                clearEquation: () => this.setState({ equationAttributes: "" }),
                // Add imperative insertion handler
                onEquationInsert: this.handleEquationInsertion,
                onDoubleClick: latexProps => {
                  const equationEditorInstance =
                    RichTextEditor.openEquationEditorInstance;

                  // Close previous instance if it exists
                  if (
                    equationEditorInstance &&
                    equationEditorInstance !== this
                  ) {
                    equationEditorInstance.closeEditor();
                  }

                  RichTextEditor.openEquationEditorInstance = this;
                  this.setState({
                    latex: latexProps,
                    isEquationEditorOpen: true,
                  });
                },
              }
            }
            linksConfig={{
              isOpen: this.state.isLinkModalOpen,
              setIsLinkModalOpen: () =>
                this.setState(prevState => ({
                  isLinkModalOpen: !prevState.isLinkModalOpen,
                })),
              closeLinkModal: this.onCloseLinkModal,
              setLinkTitle: title => this.setState({ linkModalTitle: title }),
              setLinkURL: url => this.setState({ linkModalUrl: url }),
              linkParams: this.state.linkParams,
              clearLinkParams: () => this.setState({ linkParams: {} }),
              // Add imperative insertion handler
              onLinkInsert: this.handleLinkInsertion,
            }}
            placeholder={placeholder}
            module={module}
            defaultLanguage={defaultLanguage}
            isAIModuleMarketingEnabled={this.props.isAIModuleMarketingEnabled}
            setAiModuleMarketingKey={this.props.setAiModuleMarketingKey}
            aiModuleForMarketing={aiModuleForMarketing}
            getCloudUrl={getCloudUrl}
            TheatreMode={TheatreModeV2}
            fontSize={fontSize}
            isActive={isActive}
            allowURLWithCredentials={allowURLWithCredentials}
            setToastMsg={this.props.setToastMsg}
            viewOnly={viewOnly}
            aria-describedby={this.errorId}
          />
        ) : null}

        <FormInputError
          id={this.errorId}
          {...(!_.isEmpty(errorMessage) &&
            !hideErrorMessage && {
              className: classes.errorClass,
              error: errorMessage,
            })}
        />
      </div>
    );
  };
}

const mapActionCreators = {
  setToastMsg,
  uploadFileV2,
  openLinkInNewTab,
  getAIResponse,
  isAIModuleMarketingEnabled,
  setAiModuleMarketingKey,
};

const mapStateToProps = state => {
  const currentCurriculumProgramId = getSelectedCurriculumId(state);
  const userInfo = getUserInfo();

  const promptFilters = {
    entityType: "GENERIC_TEXT",
    fieldTypes: ["ENHANCE"],
    curriculumProgramId: currentCurriculumProgramId,
  };
  return {
    orgId: userInfo.org_id,
    promptFilters,
    userInfo,
    curriculumProgramId: currentCurriculumProgramId,
  };
};

export default compose(
  I18nHOC({ resource: "common" }),
  connect(mapStateToProps, mapActionCreators, undefined, { withRef: true }),
  graphql(getAIPromptsQuery, {
    name: "getAIPrompts",
    skip: params => !params?.pluginsConfig?.ai?.isEnabled,
    options: ({ promptFilters }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        filters: promptFilters,
      },
    }),
    props({ getAIPrompts }) {
      const queryData = getAIPromptsFromCache(getAIPrompts.variables);
      const prompts = _.get(queryData, "prompts", []);

      return { prompts };
    },
  })
)(RichTextEditor);

RichTextEditor.propTypes = {
  ...UIBaseComponent.propTypes,
  height: PropTypes.number,
  plugins: PropTypes.string,
  showMenubar: PropTypes.bool,
  showStatusbar: PropTypes.bool,
  toolbarItemsSize: PropTypes.string,
  autoresizeMaxHeight: PropTypes.number,
  contentCss: PropTypes.array,
  bodyStyle: PropTypes.string,
  containerRef: PropTypes.object,
  emojiPickerPositions: PropTypes.array,
  isMediaRestricted: PropTypes.bool,
  testId: PropTypes.string,
  shouldCallOnBlur: PropTypes.bool,
  hideErrorMessage: PropTypes.bool,
  getAIResponseWrapper: PropTypes.func,
  toolbarPosition: PropTypes.string,
  isHeightAdaptive: PropTypes.bool,
  isToolbarSticky: PropTypes.bool,
  lockFieldForSchoolGroups: PropTypes.bool,
  pluginsConfig: PropTypes.object,
  emptyComponent: PropTypes.node,
};

RichTextEditor.defaultProps = {
  ...UIBaseComponent.defaultProps,
  testId: "UIComponents",
  height: 350,
  width: 864,
  plugins: "autolink autoresize lists paste image",
  containerRef: null,
  emojiPickerPositions: ["bottom", "left", "top", "right"],
  toolbar:
    "textsmall textmedium textbig  |  bold italic  underline  | alignjustify | bullist numlist | my-link emojiButton addEquation",
  showMenubar: false,
  showStatusbar: false,
  toolbarItemsSize: "small",
  autoresizeMaxHeight: 500,
  bodyStyle:
    ".mce-content-body {margin:24px !important; font-family: AvenirNext, Avenir Next, Arial, serif, sans-serif, system-ui !important;line-height: 1.42857143;} .mce-content-body div{ unicode-bidi: plaintext;}",
  isMediaRestricted: false,
  shouldCallOnBlur: true,
  hideErrorMessage: false,
  lockFieldForSchoolGroups: false,
  pluginsConfig: {},
};
